/* Estesoft.App `assets/js/utils/useToast.js`in Vega kopyası. Farklar: Toast modülü tek başına alınır
   (paket bundle'ı yerine), varsayılan süre toastr'ın 5 saniyesi. */
import Toast from 'bootstrap/js/src/toast';

const TOAST_CONTAINER_ID = 'toast-container-bs5-colored';

/* Ekranda aynı anda duran bildirim sayısı sınırlıdır: sınırın üstündekiler
   görünür alanı zaten aşıyor ve okunmuyor, ama üst üste yığılıp sayfayı
   kapatıyorlardı. Sınıra gelince en eskisi kendi animasyonuyla çıkar. Beş
   denendi ve düşürüldü: beşinci kutu ekranın yarısını kaplıyor, en alttaki
   okunmadan kapanıyordu. */
const MAX_VISIBLE = 3;

/* Glifler daireli/üçgen ailedendir ve kabın içinde değil çıplak durur. Dolu
   daire kabı (ilk hâl) mesajla ağırlık yarışına giriyordu; kap kalktığı için
   `fa-circle-check` artık daire-içinde-daire okunmuyor. Dördü birbirinden yalnız
   renkle değil siluetle de ayrılır — renk körlüğünde tek ayrım kalmasın. */
const TYPES = {
    success: { modifier: 'est-toast--success', icon: 'fa-circle-check' },
    danger: { modifier: 'est-toast--danger', icon: 'fa-circle-xmark' },
    error: { modifier: 'est-toast--danger', icon: 'fa-circle-xmark' },
    warning: { modifier: 'est-toast--warning', icon: 'fa-triangle-exclamation' },
    info: { modifier: 'est-toast--info', icon: 'fa-circle-info' }
};

function getContainer() {
    let container = document.getElementById(TOAST_CONTAINER_ID);
    if (!container) {
        container = document.createElement('div');
        container.id = TOAST_CONTAINER_ID;
        container.className = 'est-toast';
        document.body.appendChild(container);
    }
    return container;
}

/* Yalnız gösterimi tamamlanmış bildirim kırpılır: Bootstrap'in `show()` için
   sıraya aldığı geri çağrı, henüz açılmakta olan bir bildirim kapatılıp
   instance'ı bırakılırsa boş referansa dokunuyor. Kırpma bu yüzden hem yeni
   bildirimde hem `shown.bs.toast`ta çalışır — yığın hâlinde gelen bildirimler de
   sırayla oturdukça kırpılır. */
function trimStack(container) {
    const live = Array.from(container.children).filter(
        (el) => 'estShown' in el.dataset && !el.hasAttribute('data-est-closing')
    );

    while (live.length > MAX_VISIBLE) {
        const oldest = live.shift();
        oldest.setAttribute('data-est-closing', '');
        Toast.getInstance(oldest)?.hide();
    }
}

export function clearToasts() {
    const container = document.getElementById(TOAST_CONTAINER_ID);
    if (!container) return;

    Array.from(container.children).forEach((el) => {
        el.remove();
        // Instance'ı bırakmadan düğümü silmek autohide sayacını ayakta bırakır;
        // açılmakta olan bildirimde ise bırakmak Bootstrap'in bekleyen geri
        // çağrısını boş referansa düşürür (bkz. trimStack).
        if ('estShown' in el.dataset) {
            Toast.getInstance(el)?.dispose();
        }
    });
}

/* Aynı mesaj ekranda duruyorken ikincisi basılmaz, sayacı artar. Doğrulama
   akışları aynı cümleyi alan başına bir kez gönderdiği için ekranda dört özdeş
   kutu birikiyordu. */
function bumpDuplicate(container, key) {
    const existing = Array.from(container.children).find(
        (el) => el.dataset.estKey === key && !el.hasAttribute('data-est-closing')
    );
    if (!existing) return false;

    const count = Number(existing.dataset.estCount || '1') + 1;
    existing.dataset.estCount = String(count);

    let badge = existing.querySelector('.est-toast__count');
    if (!badge) {
        badge = document.createElement('span');
        badge.className = 'est-toast__count';
        // Sayaç aksiyonun/kapatmanın soluna girer; ikisi de yoksa sona eklenir
        // (`insertBefore(node, null)` = `appendChild`).
        existing.insertBefore(badge, existing.querySelector('.est-toast__action, .est-toast__close'));
    }
    badge.textContent = String(count);

    /* Sayacın kendisi kısa bir vurgu alır. Bildirimi `show()` ile yeniden
       göstermek daha görünür olurdu ama Bootstrap her `show()` çağrısında yeni
       bir autohide zamanlayıcısı kuruyor ve öncekini bırakmıyor: sahipsiz
       zamanlayıcı bırakılmış instance'ta `hide()` çağırıp konsola hata basıyordu
       (ölçüldü). Sayaç sıfırlanmadığı için tekrar bildirim özgün süresinde
       kapanır; aradan zaman geçmişse zaten yeni bildirim basılır. */
    badge.classList.remove('is-bumped');
    void badge.offsetWidth;
    badge.classList.add('is-bumped');
    return true;
}

export function showToast({
    message = '',
    title = '',
    type = 'info',
    duration = 5000, // toastr varsayılanı (timeOut)
    autohide = true,
    /* Legacy toastr seçenekleri: çağıranların bir kısmı hâlâ bunları geçiyor ve
       sessizce yok sayılıyorlardı (5 saniyelik hata bildirimi 3 saniyede
       kapanıyordu). `timeOut: 0` toastr'da "kalıcı" demektir. */
    timeOut,
    /* `{ label, onClick }` — bildirime tek bir metin butonu ekler (Geri al,
       Ayrıntı, Görüntüle). Aksiyon varken kapatma düğmesi basılmaz: iki hedef
       yan yana hangisinin bildirimi kapattığını belirsizleştiriyor. */
    action,
    closeButton
}) {
    if (typeof timeOut === 'number') {
        if (timeOut > 0) {
            duration = timeOut;
        } else {
            autohide = false;
        }
    }

    const validAction = action && action.label && typeof action.onClick === 'function' ? action : null;
    const showClose = closeButton === undefined ? !validAction : closeButton;

    const container = getContainer();
    const variant = TYPES[type] || TYPES.info;

    const key = `${variant.modifier}|${title}|${message}`;
    if (bumpDuplicate(container, key)) return;

    const toast = document.createElement('div');
    toast.className = `toast ${variant.modifier}`;
    toast.dataset.estKey = key;
    // Hata/uyarı ekran okuyucuyu keser, olumlu ve bilgi mesajı sırasını bekler.
    const isUrgent = variant.modifier !== 'est-toast--success' && variant.modifier !== 'est-toast--info';
    toast.setAttribute('role', isUrgent ? 'alert' : 'status');
    toast.setAttribute('aria-live', isUrgent ? 'assertive' : 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const icon = document.createElement('i');
    icon.className = `est-toast__icon fas ${variant.icon}`;
    icon.setAttribute('aria-hidden', 'true');

    const content = document.createElement('div');
    content.className = 'est-toast__content';

    if (title) {
        const titleEl = document.createElement('div');
        titleEl.className = 'est-toast__title';
        titleEl.textContent = title;
        content.appendChild(titleEl);
    }

    const messageEl = document.createElement('div');
    messageEl.className = 'est-toast__message';
    // Mesaj eskiden de HTML olarak basılıyordu; backend'den gelen biçimli
    // metinler bu sözleşmeye bağlı.
    messageEl.innerHTML = message;
    content.appendChild(messageEl);

    toast.appendChild(icon);
    toast.appendChild(content);

    if (validAction) {
        const actionEl = document.createElement('button');
        actionEl.type = 'button';
        actionEl.className = 'est-toast__action';
        actionEl.textContent = validAction.label;
        toast.appendChild(actionEl);
    }

    if (showClose) {
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'est-toast__close';
        close.setAttribute('data-bs-dismiss', 'toast');
        close.setAttribute('aria-label', window.vm?.$t?.('Close') || 'Close');
        close.innerHTML = '<i class="fas fa-xmark" aria-hidden="true"></i>';
        toast.appendChild(close);
    }

    container.appendChild(toast);

    const bootstrapToast = new Toast(toast, { delay: duration, autohide });

    if (validAction) {
        toast.querySelector('.est-toast__action').addEventListener('click', () => {
            // Bildirim aksiyondan sonra kapanır; `hide()` autohide sayacını da
            // temizlediği için ikinci bir kapanış tetiklenmez.
            bootstrapToast.hide();
            validAction.onClick();
        });
    }

    bootstrapToast.show();

    /* Bootstrap `showing` sınıfını geçiş süresi kadar bekledikten sonra
       kaldırıyor: bildirim o süre boyunca görünmez durup animasyona geç
       başlıyordu. Başlangıç durumunu bir reflow ile sabitleyip sınıfı bir sonraki
       karede kendimiz kaldırıyoruz — Bootstrap'in aynı işi tekrar yapması
       zararsız. */
    requestAnimationFrame(() => {
        void toast.offsetHeight;
        toast.classList.remove('showing');
    });

    toast.addEventListener('shown.bs.toast', () => {
        toast.dataset.estShown = '';
        trimStack(container);
    });

    toast.addEventListener('hidden.bs.toast', () => {
        toast.remove();
        // Instance bırakılmazsa Bootstrap'in element→instance haritasında kalır.
        if ('estShown' in toast.dataset) {
            bootstrapToast.dispose();
        }
    });
}
